import { useCallback, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import { formatPnr } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { BookedAppointment } from "reducers/conversations/conversations.reducer"

import { AssignedStaffCell, BooleanCell, DateTimePillCell, NameCell } from "components/v2/conversationList/Cells"
import ReassignDialog from "components/v2/conversationList/components/ReassignDialog/ReassignDialog"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

import { useAppSelector } from "../../../../lib/hooks"

interface OngoingErrandsListProps {
  title: string
  conversations: BookedAppointment[]
}

interface OngoingErrandsListItem extends ConversationListItem<BookedAppointment> {}

const OngoingErrandsList = ({ title, conversations }: OngoingErrandsListProps) => {
  const intl = useIntl()
  const { activeStaff } = useAppSelector(state => state.users)
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<BookedAppointment>()

  const closeReassignDialog = () => {
    setSelectedConversation(undefined)
    setReassignDialogOpen(false)
  }

  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.errand_started" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "assigned",
      name: intl.formatMessage({ id: "label.overview.assigned" })
    },
    {
      key: "paused",
      name: intl.formatMessage({ id: "label.overview.paused" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]

  const mapConversationListItem = useCallback(
    (conv: BookedAppointment): OngoingErrandsListItem => {
      const isChildConversation = conv.profile?.type === "child"
      return {
        conversation: conv,
        values: {
          pnr: isChildConversation
            ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
            : formatPnr(conv.account?.pnr, conv.metadata?.region),
          name: (
            <NameCell
              name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
              isChild={isChildConversation}
            />
          ),
          start: <DateTimePillCell value={conv.created} />,
          listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
          assigned: <AssignedStaffCell staff={activeStaff} staffId={conv.assignedStaffId} />,
          paused: <BooleanCell value={!!conv?.snoozedUntil} />,
          recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
          medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
        },
        sortValues: {
          ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent", "assigned"], {}),
          start: sortValueFunctions.created(conv),
          paused: sortValueFunctions.paused(conv)
        }
      }
    },
    [activeStaff]
  )

  const list = useMemo(
    () => conversations.map(conv => mapConversationListItem(conv)),
    [conversations, mapConversationListItem]
  )

  const openReassignDialog = (item: OngoingErrandsListItem) => {
    setSelectedConversation(item.conversation)
    setReassignDialogOpen(true)
  }

  return (
    <>
      <ConversationTable
        title={title}
        columns={columns}
        items={list}
        emptyMessage={intl.formatMessage({ id: "label.overview.list_empty" })}
        onItemClick={openReassignDialog}
      />
      {reassignDialogOpen && selectedConversation && (
        <ReassignDialog
          isOpen={reassignDialogOpen}
          conversation={selectedConversation!}
          onClose={closeReassignDialog}
        />
      )}
    </>
  )
}

export default OngoingErrandsList
